"use client";

import { useEffect, useState } from "react";

import { useSession } from "next-auth/react";

import { isDeveloperUserId } from "@/lib/feature-access";

export function useFeatureFlags() {
  const { data: session } = useSession();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const isSuperUser = Boolean(session?.user?.superUser);
  const isAdmin = session?.user?.role === "admin";
  const isSupervisor = session?.user?.role === "supervisor";
  const isDeveloper = isDeveloperUserId(session?.user?.id);
  const canAccessExternalAgentTools = isSuperUser || isAdmin || isSupervisor;

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const evaluatedFlags = {
          "ai-dialogs": true,
          "dashboard-schedule-view": isSuperUser,
          "external-agent-tools": canAccessExternalAgentTools,
          "claude-cowork-connector-guide": isSuperUser || isAdmin,
          // Choosing "phone call" delivery for an assignment is superUser-only
          // while the phone simulator is being rolled out.
          "phone-assignments": isSuperUser,
          // Adding a second buyer persona to an assignment is developer-only
          // while multi-persona simulations are being validated.
          "multi-persona-assignments": isDeveloper,
          // Suspending / reactivating a whole studio (locks every member out)
          // is a developer-only superUser action. The server action checks
          // the same thing via canSuspendStudios().
          "studio-suspension": isDeveloper && isSuperUser,
        };

        setFlags(evaluatedFlags);
      } catch (error) {
        console.error("Error fetching feature flags:", error);
        setFlags({});
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();
  }, [
    canAccessExternalAgentTools,
    isDeveloper,
    isSuperUser,
    session?.user?.role,
  ]);

  return {
    flags,
    loading,
    session,
    // Helper function to check if a feature is enabled for the current user
    isFeatureEnabled: (flagKey) => {
      return flags[flagKey] === true;
    },
    // Convenience getters
    isAdmin,
    isSupervisor,
    isDeveloper,
    isSuperUser,
    canAccessExternalAgentTools,
  };
}

// Specific hook for AI dialogs
export function useAIDialogs() {
  const { isFeatureEnabled, loading } = useFeatureFlags();

  return {
    isEnabled: isFeatureEnabled("ai-dialogs"),
    loading,
  };
}
